import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import type { LlmModelId } from "@smartmed/llm-models";
import { safeJsonParse } from "@/lib/jsonParse";
import type {
  CombineEndoscopyHistologyResult,
  ConsolidateDiagnosesResult,
  Diagnosis,
  Procedure,
  RunStageResult,
  RunStageResultGpSummary,
} from "@/types/classification";
import type { QueryData } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";

const getStageTestDataQuery = (stageType: "endoscopyStageResult" | "histologyStageResult" | "gpSummaryStageResult") => {
  const tableName =
    stageType === "endoscopyStageResult"
      ? "test_endoscopy_results"
      : stageType === "histologyStageResult"
        ? "test_histology_results"
        : "test_gp_summary_results";
  return supabase.from(tableName).select(
    `*,
      test_data:medical_text_examples (
        id,
        text,
        test_type,
        sequence_number,
        label,
        expected_procedures (
          code_id,
          opcs4_codes (
            code,
            description
          )
        ),
        expected_diagnosis (
          code_id,
          is_optional,
          icd10_codes:icd10_codes (
            code,
            description
          )
        ),
        expected_co_morbidities (
          code_id,
          is_optional,
          icd10_codes:icd10_codes (
            code,
            description
          )
        )
      )
    `,
  );
};

type TestStageData = QueryData<ReturnType<typeof getStageTestDataQuery>>[number];

function getTestCombineEndoHistoQuery() {
  return supabase.from("test_combine_endo_histo_results").select(
    `
      *
    `,
  );
}
type CombineEndoHistoData = QueryData<ReturnType<typeof getTestCombineEndoHistoQuery>>[number];

function getTestConsolidateDiagnosesQuery() {
  return supabase.from("test_consolidate_diagnoses_results").select(
    `
      *
    `,
  );
}
type ConsolidateDiagnosesData = QueryData<ReturnType<typeof getTestConsolidateDiagnosesQuery>>[number];

export type RunStage = Omit<TestStageData, "result"> & {
  id: string;
  result: RunStageResult | null;
};

export type RunStageGpSummary = Omit<TestStageData, "result"> & {
  id: string;
  result: RunStageResultGpSummary | null;
};

export type CombineEndoHistoStage = Omit<CombineEndoHistoData, "result"> & {
  id: string;
  result: CombineEndoscopyHistologyResult | null;
};

export type ConsolidateDiagnosesStage = Omit<ConsolidateDiagnosesData, "result"> & {
  id: string;
  result: ConsolidateDiagnosesResult | null;
};

const safeParseJsonRunResult = <T extends RunStageResult | RunStageResultGpSummary>(result: string): T | null => {
  const parsedResult = safeJsonParse(result);

  if (!parsedResult) {
    console.error("Failed to parse run result", result);
    return null;
  }

  return parsedResult as T;
};

const transformTestRunStageResult = <
  TInput extends { result: Json },
  TResult extends
    | RunStageResult
    | RunStageResultGpSummary
    | CombineEndoscopyHistologyResult
    | ConsolidateDiagnosesResult,
>(
  stageRow: TInput | null,
) => {
  if (!stageRow) {
    return null;
  }

  const parsedResult = safeParseJsonRunResult<TResult>(stageRow.result as string);
  if (!parsedResult) {
    return null;
  }

  return {
    ...stageRow,
    result: parsedResult,
  } as Omit<TInput, "result"> & { result: TResult };
};

const getTestRunQuery = () => {
  return supabase.from("test_classification_run_results").select(`
    *,
    testEncounterResult:test_encounter_results (
      id,
      review_summary,
      should_flag_for_review,
      patient_encounter_id,
      test_run_hrg:test_run_hrgs (
        id,
        hrg:hrgs (
          id,
          code,
          description
        )
      )
    ),
    diagnoses_raw:test_run_diagnoses (
      id,
      diagnosis_code,
      order_index
    ),
    procedures_raw:test_run_procedures (
      id,
      procedure_code,
      order_index
    )
  `);
};
type TestRun = NonNullable<Awaited<ReturnType<typeof getTestRunQuery>>["data"]>[number];

export type RunResultWithStages = Omit<TestRun, "llm_model_id"> & {
  llm_model_id: LlmModelId | null;
  endoscopyStageResult: RunStage | null;
  histologyStageResult: RunStage | null;
  gpSummaryStageResult: RunStageGpSummary | null;
  combineEndoHistoStageResult: CombineEndoHistoStage | null;
  consolidateDiagnosesStageResult: ConsolidateDiagnosesStage | null;
  diagnoses: Diagnosis[];
  procedures: Procedure[];
  testEncounterResult: {
    id: string;
    patient_encounter_id: string;
    test_run_hrg: {
      id: string;
      hrg: {
        id: string;
        code: string;
        description: string | null;
      };
    }[];
  } | null;
};

const useRunResultWithStages = ({ runId }: { runId: string | undefined }) => {
  const testDataQueryResult = useQuery<RunResultWithStages | null>({
    queryKey: ["run-result-with-stages", runId],
    enabled: !!runId,
    queryFn: async () => {
      const { data: testRunData, error: selectTestDataError } = await getTestRunQuery().eq("id", runId!).single();

      if (!testRunData || selectTestDataError) {
        return null;
      }
      const resultDiagnosisCodes = testRunData.diagnoses_raw.map((diagnosis) => diagnosis.diagnosis_code);
      const resultProcedureCodes = testRunData.procedures_raw.map((procedure) => procedure.procedure_code);

      const [
        endoscopyStageResult,
        histologyStageResult,
        gpSummaryStageResult,
        combineEndoHistoStageResult,
        consolidateDiagnosesStageResult,
        icd10Codes,
        opcs4Codes,
      ] = await Promise.all([
        getStageTestDataQuery("endoscopyStageResult").eq("test_classification_run_results_id", runId!).maybeSingle(),
        getStageTestDataQuery("histologyStageResult").eq("test_classification_run_results_id", runId!).maybeSingle(),
        getStageTestDataQuery("gpSummaryStageResult").eq("test_classification_run_results_id", runId!).maybeSingle(),
        getTestCombineEndoHistoQuery().eq("test_classification_run_results_id", runId!).maybeSingle(),
        getTestConsolidateDiagnosesQuery().eq("test_classification_run_results_id", runId!).maybeSingle(),
        supabase
          .from("icd10_codes")
          .select(
            `
            code,
            description,
            max_age,
            min_age
          `,
          )
          .in("code", resultDiagnosisCodes),
        supabase
          .from("opcs4_codes")
          .select(
            `
            code,
            description
          `,
          )
          .in("code", resultProcedureCodes),
      ]);

      // enrich raw result diagnoses and procedures with the db codes
      // this also maintain order of the diagnoses and procedures
      const enrichedDiagnoses = testRunData.diagnoses_raw
        .map((resultDiagnosis) => {
          const dbCode = icd10Codes.data?.find((code) => code.code === resultDiagnosis.diagnosis_code);

          if (!dbCode) {
            console.error("No db code found for diagnosis", resultDiagnosis);
            return null;
          }

          return {
            id: resultDiagnosis.id,
            code: resultDiagnosis.diagnosis_code,
            description: dbCode.description,
            type: "ICD-10" as const,
          };
        })
        .filter((i) => i !== null);

      const enrichedProcedures = testRunData.procedures_raw
        .map((resultProcedure) => {
          const dbCode = opcs4Codes.data?.find((code) => code.code === resultProcedure.procedure_code);

          if (!dbCode) {
            console.error("No db code found for procedure", resultProcedure);
            return null;
          }

          return {
            id: resultProcedure.id,
            code: resultProcedure.procedure_code,
            description: dbCode.description,
            type: "OPCS-4" as const,
          };
        })
        .filter((i) => i !== null);

      return {
        ...testRunData,
        llm_model_id: testRunData.llm_model_id as LlmModelId | null,
        diagnoses: enrichedDiagnoses,
        procedures: enrichedProcedures,
        endoscopyStageResult: transformTestRunStageResult<TestStageData, RunStageResult>(endoscopyStageResult.data),
        histologyStageResult: transformTestRunStageResult<TestStageData, RunStageResult>(histologyStageResult.data),
        gpSummaryStageResult: transformTestRunStageResult<TestStageData, RunStageResultGpSummary>(
          gpSummaryStageResult.data,
        ),
        combineEndoHistoStageResult: transformTestRunStageResult<CombineEndoHistoData, CombineEndoscopyHistologyResult>(
          combineEndoHistoStageResult.data,
        ),
        consolidateDiagnosesStageResult: transformTestRunStageResult<
          ConsolidateDiagnosesData,
          ConsolidateDiagnosesResult
        >(consolidateDiagnosesStageResult.data),
      };
    },
  });

  return testDataQueryResult;
};

export default useRunResultWithStages;
